import React, { useState, useCallback, useEffect } from "react";
import { Col, Container, Row, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import AuditLogFilters from "../components/AuditLog/AuditLogFilters";
import AuditLogTable from "../components/AuditLog/AuditLogTable";
import PaginationComponent from "../components/PaginationComponent";
import AdministrationIcon from "../assets/images/two_gears_rgb_red.svg";
import searchIcon from "../assets/images/search_rgb_red.svg";
import "../assets/styles/auditLog.scss";
import api from "../services/httpClient";
import usePersistedFilters from "../hooks/usePersistedFilters";
import usePersistedPageSize from "../hooks/usePersistedPageSize";

const AuditLogList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = usePersistedPageSize("auditLog", 20);
  const { filters, handleFilterChange, clearFilters, hasActiveFilters } = usePersistedFilters("auditLog");
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [sortConfig, setSortConfig] = useState({ key: "Date", direction: "desc" });

  const [data, setData] = useState({
    rows: [],
    total: 0,
    totalPages: null,
  });

  const fetchData = useCallback(
    async (
      pageNumber = currentPage,
      pageSize = itemsPerPage,
      filtersToApply = appliedFilters,
      sortConfigToApply = sortConfig
    ) => {
      try {
        const offset = (pageNumber - 1) * pageSize;

        const requestBody = {
          sort: sortConfigToApply.key,
          order: sortConfigToApply.direction,
          offset: offset,
          limit: pageSize,
          search: {
            action: filtersToApply.action !== undefined && filtersToApply.action !== "" ? Number(filtersToApply.action) : null,
            userLogin: filtersToApply.userLogin || "",
            query: filtersToApply.query || "",
            dateFrom: filtersToApply.dateFrom || "",
            dateTo: filtersToApply.dateTo || "",
          },
        };

        const response = await api.post(`/AuditLog/List`, requestBody);

        const result = response.data?.data || response.data;
        const rows = Array.isArray(result.items) ? result.items : (Array.isArray(result.rows) ? result.rows : []);
        const total = result.itemsCount ?? result.total ?? 0;
        const totalPages = result.totalPages ?? null;
        setData({ rows, total, totalPages });
      } catch (error) {
        console.error("Error fetching audit log list:", error);
        setData({ rows: [], total: 0 });
      }
    },
    [currentPage, itemsPerPage, appliedFilters, sortConfig]
  );

  useEffect(() => {
    fetchData();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleImmediateFilterChange = (field, value) => {
    handleFilterChange(field, value);
    const updatedFilters = { ...filters, [field]: value };
    setAppliedFilters(updatedFilters);
    fetchData(1, itemsPerPage, updatedFilters);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    fetchData(1, itemsPerPage, filters);
    setCurrentPage(1);
  };

  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    fetchData(currentPage, itemsPerPage, appliedFilters, newSortConfig);
  };

  const renderSearchTooltip = (props) => (
    <Tooltip id="search-tooltip" {...props}>
      Szukaj
    </Tooltip>
  );

  return (
    <div className="audit-log-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img
              src={AdministrationIcon}
              alt="Audit Log Icon"
              style={{ width: "30px", height: "30px" }}
            />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              Dziennik logów
            </h3>
          </Col>
          <Col xs="auto">
            <OverlayTrigger
              placement="top"
              delay={{ show: 250, hide: 400 }}
              overlay={renderSearchTooltip}
            >
              <Button className="addresses-button" onClick={handleSearch}>
                <img
                  src={searchIcon}
                  alt="Search"
                  style={{ width: "20px", height: "20px" }}
                />
              </Button>
            </OverlayTrigger>
          </Col>
        </Row>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <AuditLogFilters onFilterChange={handleFilterChange} onSearch={handleSearch} onImmediateFilterChange={handleImmediateFilterChange} filters={filters} />
          </Col>
          <Col className="flex-grow-1" />
        </Row>
        <Row className="justify-content-end mb-3">
          <Col xs="auto">
            <Button
              variant={hasActiveFilters ? "success" : "outline-secondary"}
              size="sm"
              className="me-3"
              onClick={() => { clearFilters(); setAppliedFilters({}); fetchData(1, itemsPerPage, {}); setCurrentPage(1); }}
            >
              Wyczyść filtry
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <AuditLogTable
              data={data.rows}
              onSortChange={handleSortChange}
            />
          </Col>
        </Row>
        <Row className="align-items-center mt-4">
          <PaginationComponent
            itemsPerPage={itemsPerPage}
            dataCount={data.rows.length}
            totalCount={data.total}
            totalPages={data.totalPages}
            pageNumber={currentPage}
            onPageChange={(page) => {
              setCurrentPage(page);
              fetchData(page, itemsPerPage);
            }}
            onItemsPerPageChange={(pageSize) => {
              const size = Number(pageSize);
              setItemsPerPage(size);
              setCurrentPage(1);
              fetchData(1, size);
            }}
            itemsPerPageOptions={[20, 100, 500]}
          />
        </Row>
      </Container>
    </div>
  );
};

export default AuditLogList;
